"use client";

import { Bot, Search, Brain, Sparkles, FileText } from "lucide-react";

interface ThinkingIndicatorProps {
  status?: { step: string; message: string } | null;
}

const STEP_CONFIG: Record<string, { icon: typeof Search; accent: string }> = {
  connect: { icon: Sparkles, accent: "from-gray-400 to-gray-500" },
  parse: { icon: FileText, accent: "from-violet-400 to-violet-600" },
  search: { icon: Search, accent: "from-teal-400 to-teal-600" },
  analyze: { icon: Brain, accent: "from-violet-500 to-violet-700" },
  generate: { icon: Sparkles, accent: "from-violet-400 to-violet-700" },
};

const STEP_ORDER = ["connect", "parse", "search", "analyze", "generate"];

export function ThinkingIndicator({ status }: ThinkingIndicatorProps) {
  const step = status?.step || "connect";
  const message = status?.message || "처리 중";
  const config = STEP_CONFIG[step] || STEP_CONFIG.connect;
  const Icon = config.icon;
  const currentIdx = STEP_ORDER.indexOf(step);

  return (
    <div className="animate-in fade-in flex gap-2.5 px-4 py-2 duration-300">
      <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-50">
        <Bot className="h-3.5 w-3.5 text-violet-600" strokeWidth={2} />
      </div>
      <div className="w-full max-w-[85%]">
        <div className="thinking-card overflow-hidden rounded-2xl rounded-bl-md px-5 py-4">
          <div className="flex items-center gap-3.5">
            {/* 오브 */}
            <div className="thinking-orb-wrap relative h-11 w-11 shrink-0">
              {/* 외곽 글로우 */}
              <div className="thinking-glow-ring absolute inset-[-4px] rounded-full" />
              {/* 메인 오브 */}
              <div
                className={`absolute inset-0 rounded-full bg-gradient-to-br ${config.accent} thinking-orb-main`}
              />
              {/* 내부 반사 */}
              <div className="absolute inset-[2px] rounded-full bg-gradient-to-br from-white/25 via-transparent to-transparent" />
              {/* 회전 궤도 */}
              <div className="thinking-orbit absolute inset-[-3px] rounded-full border-[1.5px] border-transparent border-t-violet-300/60" />
              <div className="thinking-orbit-reverse absolute inset-[-6px] rounded-full border border-transparent border-b-violet-200/30" />
              {/* 아이콘 */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon className="h-4.5 w-4.5 text-white drop-shadow-sm" strokeWidth={2} />
              </div>
            </div>

            {/* 텍스트 + 프로그레스 */}
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <span
                className="animate-in fade-in slide-in-from-left-2 text-sm font-semibold text-gray-700 duration-300"
                key={step}
              >
                {message}
              </span>
              {/* 단계 프로그레스 */}
              <div className="flex items-center gap-[3px]">
                {STEP_ORDER.map((s, i) => (
                  <div
                    key={s}
                    className={`h-[3px] flex-1 rounded-full transition-all duration-700 ease-out ${
                      i < currentIdx
                        ? "bg-violet-500"
                        : i === currentIdx
                          ? "thinking-bar-active bg-violet-400"
                          : "bg-gray-200"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
